import React, { useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Portal,
  Select,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Done, Loop, Schedule } from '@mui/icons-material';
import { nbNO } from '@mui/x-data-grid-pro/locales';
import {
  DataGridPro,
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterInputValueProps,
  GridFilterOperator,
  GridRenderCellParams,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid-pro';
import { StyledLink } from '../Components';
import { AppOverviewItemFragment, ApplicationStatus } from '../__generated__/graphql';

const STATUS_TEXT: Record<ApplicationStatus, string> = {
  [ApplicationStatus.New]: 'Ny',
  [ApplicationStatus.Active]: 'Aktiv',
};

export const statusText = (status: ApplicationStatus) => STATUS_TEXT[status];

/** The same icons the guides use for Ny and Aktiv (StatusChip in GuideStatusCells). */
export function AppStatusIcon({ status, fontSize }: { status: ApplicationStatus; fontSize?: 'small' | 'medium' }) {
  return status === ApplicationStatus.Active ? (
    <Tooltip title="Aktiv">
      <Done fontSize={fontSize} sx={{ color: 'success.main', verticalAlign: 'middle' }} />
    </Tooltip>
  ) : (
    <Tooltip title="Ny">
      <Schedule fontSize={fontSize} sx={{ color: 'text.secondary', verticalAlign: 'middle' }} />
    </Tooltip>
  );
}

// Tags work the way they do in Planlegging (backlog/Overview.tsx): the cell holds the keys, and the
// filter picks any number of them from a list and keeps the rows that have them all.
type TagKey = 'revision';

const TAG_OPTIONS: { key: TagKey; label: string }[] = [{ key: 'revision', label: 'Under revisjon' }];

const tagLabel = (key: TagKey) => TAG_OPTIONS.find((o) => o.key === key)?.label ?? key;

function getTagsForRow(app: AppOverviewItemFragment): TagKey[] {
  const tags: TagKey[] = [];
  if (app.application.underRevision) tags.push('revision');
  return tags;
}

function TagFilterInput({ item, applyValue }: GridFilterInputValueProps) {
  const value: TagKey[] = Array.isArray(item.value) ? item.value : [];
  return (
    <FormControl variant="standard" fullWidth sx={{ minWidth: 180 }}>
      <InputLabel shrink>Tagger</InputLabel>
      <Select
        multiple
        value={value}
        onChange={(e) => {
          const next = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
          applyValue({ ...item, value: next });
        }}
        renderValue={(selected) => (selected as TagKey[]).map(tagLabel).join(', ')}>
        {TAG_OPTIONS.map((opt) => (
          <MenuItem key={opt.key} value={opt.key}>
            <Checkbox checked={value.includes(opt.key)} size="small" />
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

const tagAllOperator: GridFilterOperator<AppOverviewItemFragment, TagKey[]> = {
  label: 'inneholder alle',
  value: 'containsAll',
  getApplyFilterFn: (filterItem) => {
    const selected: TagKey[] = Array.isArray(filterItem.value) ? filterItem.value : [];
    if (selected.length === 0) return null;
    return (rowValue) => selected.every((t) => (rowValue ?? []).includes(t));
  },
  InputComponent: TagFilterInput,
};

const STANDALONE = 'Selvstendig';
const EMBEDDABLE = 'I anvisning';
const BOTH = 'Selvstendig og i anvisning';

/** How the app reaches readers: on its own, inside guides, or both. One value each, so the filter list is exact. */
export const appType = (app: Pick<AppOverviewItemFragment, 'isStandalone' | 'isEmbeddable'>) =>
  app.isStandalone && app.isEmbeddable ? BOTH : app.isStandalone ? STANDALONE : app.isEmbeddable ? EMBEDDABLE : '';

function RevisionIcon({ fontSize = 'small' }: { fontSize?: 'small' | 'inherit' }) {
  return (
    <Tooltip title="Under revisjon">
      <Loop fontSize={fontSize} sx={{ verticalAlign: 'middle' }} />
    </Tooltip>
  );
}

/** On a narrow screen the name carries the hidden columns in a second line, so the row still says what the table does. */
function NameWithDetails({ app }: { app: AppOverviewItemFragment }) {
  const guides = app.guideCount === 1 ? '1 anvisning' : `${app.guideCount} anvisninger`;
  return (
    <Box sx={{ minWidth: 0 }}>
      <StyledLink to={`/app/${app.id}`}>{app.name}</StyledLink>
      <Typography variant="caption" color="textSecondary" component="div">
        {app.application.underRevision && (
          <>
            <RevisionIcon fontSize="inherit" />{' '}
          </>
        )}
        {[app.category, app.application.owner?.name, guides].filter(Boolean).join(' · ')}
      </Typography>
    </Box>
  );
}

/** What a narrow screen leaves out; the name column shows them instead. */
const HIDDEN_WHEN_NARROW: GridColumnVisibilityModel = { tags: false, owner: false, category: false, type: false, guideCount: false };

/** The user's own choice for the columns a narrow screen hides, so a change made there leaves them alone. */
const wideOnly = (model: GridColumnVisibilityModel): GridColumnVisibilityModel =>
  Object.fromEntries(Object.keys(HIDDEN_WHEN_NARROW).map((field) => [field, model[field] ?? true]));

const columns = (categories: string[], narrow: boolean): GridColDef<AppOverviewItemFragment>[] => [
  {
    field: 'status',
    // On a narrow screen the column is only as wide as the icon, which leaves no room for a header or its menu.
    headerName: narrow ? '' : 'Status',
    description: 'Status',
    width: narrow ? 48 : 80,
    disableColumnMenu: narrow,
    type: 'singleSelect',
    valueOptions: Object.values(STATUS_TEXT),
    // The text drives the filter list and the quick search; the cell shows the icon.
    valueGetter: (_, row) => statusText(row.application.status),
    renderCell: (params: GridRenderCellParams<AppOverviewItemFragment, string>) => <AppStatusIcon status={params.row.application.status} />,
  },
  {
    field: 'name',
    headerName: 'Navn',
    flex: 2,
    // The other columns have fixed widths; without a floor the name is the one squeezed to nothing.
    minWidth: 220,
    renderCell: (params: GridRenderCellParams<AppOverviewItemFragment, string>) =>
      narrow ? <NameWithDetails app={params.row} /> : <StyledLink to={`/app/${params.row.id}`}>{params.row.name}</StyledLink>,
  },
  {
    field: 'tags',
    headerName: 'Tagger',
    sortable: false,
    width: 140,
    valueGetter: (_, row) => getTagsForRow(row),
    filterOperators: [tagAllOperator],
    // The cell holds tag keys, so search on their labels to keep the quick search finding "revisjon".
    getApplyQuickFilterFn: (search) => (value: TagKey[]) => (value ?? []).some((t) => tagLabel(t).toLowerCase().includes(String(search).toLowerCase())),
    renderCell: (params: GridRenderCellParams<AppOverviewItemFragment, TagKey[]>) => (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%' }}>
        {params.value?.includes('revision') && <RevisionIcon />}
      </Stack>
    ),
  },
  { field: 'owner', headerName: 'Ansvarlig', width: 180, valueGetter: (_, row) => row.application.owner?.name ?? '' },
  { field: 'category', headerName: 'Kategori', width: 140, type: 'singleSelect', valueOptions: categories },
  {
    field: 'type',
    headerName: 'Type',
    width: 210,
    type: 'singleSelect',
    valueOptions: [STANDALONE, EMBEDDABLE, BOTH],
    valueGetter: (_, row) => appType(row),
  },
  { field: 'guideCount', headerName: 'Anvisninger', type: 'number', width: 120 },
];

/** The apps as a grid with a quick search above it, the way Revisjoner does it. */
export function AppTable({ apps, loading }: { apps: AppOverviewItemFragment[]; loading: boolean }) {
  const categories = useMemo(
    () => [...new Set(apps.map((x) => x.category).filter((x): x is string => !!x))].sort((a, b) => a.localeCompare(b)),
    [apps],
  );
  const theme = useTheme();
  // Below md the fixed columns alone are wider than the grid, with the menu open.
  const narrow = useMediaQuery(theme.breakpoints.down('md'));
  const appColumns = useMemo(() => columns(categories, narrow), [categories, narrow]);
  // The columns hidden from the column menu, kept apart from the ones a narrow screen hides so a wider
  // screen brings back exactly what the user had.
  const [visibility, setVisibility] = useState<GridColumnVisibilityModel>({});

  return (
    <Stack spacing={2} marginTop={2}>
      <Box id="app-filter-panel" />
      <DataGridPro
        rows={apps}
        loading={loading}
        columns={appColumns}
        density="compact"
        disableRowSelectionOnClick
        columnVisibilityModel={narrow ? { ...visibility, ...HIDDEN_WHEN_NARROW } : visibility}
        onColumnVisibilityModelChange={(model) => setVisibility(narrow ? { ...model, ...wideOnly(visibility) } : model)}
        getRowHeight={() => (narrow ? 'auto' : null)}
        sx={narrow ? { '& .MuiDataGrid-cell': { py: 0.75 } } : undefined}
        localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
        pagination
        slots={{ toolbar: QuickFilterToolbar }}
        initialState={{
          pagination: { paginationModel: { pageSize: 50 } },
          sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
          // The quick search still finds a category or an owner where those columns are hidden.
          filter: { filterModel: { items: [], quickFilterExcludeHiddenColumns: false } },
        }}
      />
    </Stack>
  );
}

function QuickFilterToolbar() {
  return (
    <Portal container={() => document.getElementById('app-filter-panel')!}>
      <GridToolbarQuickFilter sx={{ width: '100%' }} />
    </Portal>
  );
}
